<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { useKioskApi } from '../composables/useKioskApi'
import type { WorkerSummary } from '../types/kiosk'

const { t } = useI18n()
const { fetchWorkerSummary } = useKioskApi()

const WORKER_KEY = 'yuksalish.kiosk.worker_id'

function readStoredWorker(): number | null {
  try {
    const value = localStorage.getItem(WORKER_KEY)
    return value ? Number(value) : null
  } catch {
    return null
  }
}

const reportDate = ref(new Date().toLocaleDateString('en-CA'))
const summary = ref<WorkerSummary | null>(null)
const loading = ref(true)
const error = ref('')

const workerId = ref<number | null>(readStoredWorker())
const workerName = ref('')

async function load() {
  loading.value = true
  error.value = ''
  if (workerId.value == null) {
    loading.value = false
    return
  }
  try {
    summary.value = await fetchWorkerSummary(workerId.value, reportDate.value)
    workerName.value = summary.value.employee_name ?? ''
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

watch(reportDate, load)
onMounted(load)

const totals = computed(() => summary.value?.totals ?? { completed: 0, rejected: 0, rework: 0 })
const earnings = computed(() => summary.value?.earnings)

function formatMoney(value: number): string {
  return value.toLocaleString('uz-UZ', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('kiosk.earnings.eyebrow') }}</p>
      <h2>{{ t('kiosk.earnings.title') }}</h2>
      <p>{{ workerName ? `${workerName} · ` : '' }}{{ t('kiosk.earnings.description') }}</p>
    </div>
    <label class="date-field">
      <span>{{ t('kiosk.earnings.date') }}</span>
      <input v-model="reportDate" type="date" class="date-input" />
    </label>
  </div>

  <!-- Loading -->
  <div v-if="loading" class="state-message"><p>{{ t('kiosk.earnings.loading') }}</p></div>

  <!-- Error -->
  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('kiosk.earnings.retry') }}</button>
  </div>

  <!-- Empty -->
  <div v-else-if="!summary || (summary.by_stage ?? []).length === 0" class="state-message">
    <p>{{ t('kiosk.earnings.noData') }}</p>
  </div>

  <template v-else>
    <div class="earnings-grid">
      <div class="summary-card">
        <span class="summary-value">{{ totals.completed.toLocaleString() }}</span>
        <span class="summary-label">{{ t('kiosk.earnings.completedPairs') }}</span>
      </div>
      <div class="summary-card">
        <span class="summary-value">{{ (earnings?.breakdown ?? []).length }}</span>
        <span class="summary-label">{{ t('kiosk.kpi.stage') }}s</span>
      </div>
      <div class="summary-card highlight">
        <span class="summary-value">{{ formatMoney(earnings?.estimated_total ?? 0) }} {{ earnings?.currency_code ?? 'UZS' }}</span>
        <span class="summary-label">{{ t('kiosk.earnings.estimatedEarnings') }}</span>
      </div>
    </div>

    <!-- Per-stage breakdown -->
    <div v-if="(earnings?.breakdown ?? []).length" class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('kiosk.kpi.stage') }}</th>
            <th>{{ t('kiosk.earnings.pieceRate') }}</th>
            <th>{{ t('kiosk.earnings.estimatedEarnings') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in earnings?.breakdown ?? []" :key="item.stage_code">
            <td>{{ item.stage_code }}</td>
            <td class="cell-qty">{{ formatMoney(item.rate_per_produced_unit) }} {{ earnings?.currency_code ?? 'UZS' }}</td>
            <td class="cell-qty">{{ formatMoney(item.amount) }} {{ earnings?.currency_code ?? 'UZS' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <p class="estimate-note">{{ t('kiosk.earnings.estimatedNote') }}</p>
  </template>
</template>

<style scoped>
.page-heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  gap: 16px;
}
.eyebrow {
  margin: 0 0 4px;
  color: #65736e;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
h2 { margin: 0 0 4px; font-size: 22px; font-weight: 700; }
.page-heading p { margin: 0; color: #5f6b6a; font-size: 14px; }
.date-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #65736e;
}
.date-input {
  padding: 8px 14px;
  border: 1px solid #d4d8d5;
  border-radius: 8px;
  font-size: 13px;
  background: #fff;
}

.state-message { padding: 40px 0; text-align: center; color: #5f6b6a; }
.state-message.error { color: #b33a3a; }

.earnings-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}
.summary-card {
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 12px;
  padding: 20px 24px;
  text-align: center;
}
.summary-card.highlight { border-color: #147a8a; background: #eaf4f5; }
.summary-value { display: block; font-size: 26px; font-weight: 800; color: #1a2b26; }
.summary-label { display: block; font-size: 11px; color: #65736e; text-transform: uppercase; font-weight: 600; margin-top: 4px; }

.data-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 16px;
}
.data-table th {
  text-align: left;
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #65736e;
  background: #f6f8f7;
  border-bottom: 1px solid #e2e6e3;
}
.data-table td {
  padding: 12px 16px;
  font-size: 13px;
  color: #1a2b26;
  border-bottom: 1px solid #f0f2f1;
}
.data-table tr:last-child td { border-bottom: none; }
.cell-qty { font-weight: 700; }

.estimate-note {
  padding: 12px 16px;
  background: #f6f8f7;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  font-size: 12px;
  color: #65736e;
}

.btn-primary {
  display: inline-block;
  margin-top: 12px;
  padding: 8px 20px;
  background: #147a8a;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.btn-primary:hover { background: #0f6573; }

/* Touch-kiosk: portrait 15" panel or compact width. */
@media (orientation: portrait), (max-width: 1180px) {
  .page-heading {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    margin-bottom: 16px;
  }
  .date-input {
    min-height: 48px;
    font-size: 15px;
  }
  .earnings-grid {
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 10px;
    margin-bottom: 16px;
  }
  .summary-card { padding: 18px 16px; }
  .summary-value { font-size: 26px; }
  .summary-label { font-size: 12px; }
  .data-table th,
  .data-table td { padding: 14px 16px; font-size: 15px; }
  .data-table th { font-size: 12px; }
  .estimate-note { font-size: 14px; }
  .btn-primary {
    min-height: 48px;
    padding: 10px 24px;
    font-size: 15px;
  }
}

@media (max-width: 600px) {
  .earnings-grid { grid-template-columns: 1fr; }
}
</style>
